import { useContext, useLayoutEffect, useMemo, useState } from 'react';
import { FilterContextData } from 'contexts/FilterContext';
import { trackEventMixpanel } from 'utils/mixpanel';
import { GLOBAL_NAV_PANEL_CLOSE, GLOBAL_NAV_PANEL_OPEN, GLOBAL_NAV_RAIL_TOGGLE } from 'utils/mixpanelEventNames';

interface Args {
    /** `commonConfig.useNavbarV2`. When false every value here is inert and V1 layout applies. */
    enabled: boolean;
    /** `commonConfig.useGlobalFilterV2`. The two filter generations occupy different widths. */
    useGlobalFilterV2: boolean;
    /** Routes that render no global filter at all, so the funnel has nothing to toggle. */
    removeGlobalFilter: boolean;
}

export interface NavShellState {
    isRailOpen: boolean;
    isNavPanelOpen: boolean;
    isFilterPanelOpen: boolean;
    /** Left edge of the rail: the rail width, or 0 once the hamburger closes it. */
    railLeftOffset: string;
    /** Rail width plus whichever panel occupies the shared slot, when one is open. */
    shellLeftOffset: string;
    /** `#main-container` margin-left under V2 — undefined when V1 utility classes still own it. */
    contentMarginLeft?: string;
    toggleRail: () => void;
    toggleNavPanel: () => void;
    toggleFilterPanel: () => void;
}

const RAIL_WIDTH = 'var(--nav-rail-width)';
const PANEL_WIDTH = 'var(--global-filter-v2-width)';
// The V1 filter is two columns — the sidebar plus the expand strip beside it — so it is wider
// than the shared slot. Matches the `global-filter-margin-left` utility V1 layout uses.
const FILTER_V1_WIDTH = 'calc(var(--global-filter-width) + var(--global-filter-expand-width))';

/**
 * Shell state for Navbar V2: rail visibility plus which of the two panels owns the shared
 * 220px slot. The navigation panel is local state; the global filter keeps living in
 * FilterContext, so this only enforces that the two are never open at once.
 */
export const useNavShellState = ({ enabled, useGlobalFilterV2, removeGlobalFilter }: Args): NavShellState => {
    const { isGlobalFilterCollapsed, setIsGlobalFilterCollapsed } = useContext(FilterContextData);
    const [isRailOpen, setIsRailOpen] = useState(true);
    const [isNavPanelOpen, setIsNavPanelOpen] = useState(false);

    // V2 opens with the rail and no panel. FilterContext seeds `isGlobalFilterCollapsed` to
    // false (filter open) for V1 and is deliberately left untouched, so the V2 default is
    // corrected here, before paint so the panel never flashes open. Both deps are stable, so
    // this runs once — re-running would reopen the filter behind the user's back every time
    // they closed it.
    useLayoutEffect(() => {
        if (enabled) setIsGlobalFilterCollapsed(true);
    }, [enabled, setIsGlobalFilterCollapsed]);

    const isFilterPanelOpen = enabled && isRailOpen && !removeGlobalFilter && !isGlobalFilterCollapsed;
    const isNavPanelVisible = enabled && isRailOpen && isNavPanelOpen;
    const filterPanelWidth = useGlobalFilterV2 ? PANEL_WIDTH : FILTER_V1_WIDTH;

    const railLeftOffset = isRailOpen ? RAIL_WIDTH : '0px';

    // The two panels are mutually exclusive, so at most one width joins the rail here
    const shellLeftOffset = useMemo(() => {
        const parts: string[] = [];
        if (isRailOpen) parts.push(RAIL_WIDTH);
        if (isNavPanelVisible) parts.push(PANEL_WIDTH);
        else if (isFilterPanelOpen) parts.push(filterPanelWidth);
        return parts.length ? `calc(${parts.join(' + ')})` : '0px';
    }, [isRailOpen, isNavPanelVisible, isFilterPanelOpen, filterPanelWidth]);

    const toggleNavPanel = () => {
        const next = !isNavPanelVisible;
        setIsNavPanelOpen(next);
        if (next) setIsGlobalFilterCollapsed(true);
        trackEventMixpanel(next ? GLOBAL_NAV_PANEL_OPEN : GLOBAL_NAV_PANEL_CLOSE, { panel: 'navigation' });
    };

    const toggleFilterPanel = () => {
        const next = !isFilterPanelOpen;
        setIsGlobalFilterCollapsed(!next);
        if (next) setIsNavPanelOpen(false);
        trackEventMixpanel(next ? GLOBAL_NAV_PANEL_OPEN : GLOBAL_NAV_PANEL_CLOSE, { panel: 'global-filter' });
    };

    // The hamburger takes the rail and whatever panel is open down together
    const toggleRail = () => {
        const next = !isRailOpen;
        if (!next) {
            setIsNavPanelOpen(false);
            setIsGlobalFilterCollapsed(true);
        }
        setIsRailOpen(next);
        trackEventMixpanel(GLOBAL_NAV_RAIL_TOGGLE, { open: next });
    };

    return {
        isRailOpen,
        isNavPanelOpen: isNavPanelVisible,
        isFilterPanelOpen,
        railLeftOffset,
        shellLeftOffset,
        contentMarginLeft: enabled ? shellLeftOffset : undefined,
        toggleRail,
        toggleNavPanel,
        toggleFilterPanel,
    };
};

export default useNavShellState;
